import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import { FcMusic } from 'react-icons/fc';

export default function UploadMusic() {
  const auth = getAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
  });
  const [musicFile, setMusicFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // Check if user is authenticated
  useEffect(() => {
    if (!auth.currentUser) {
      toast.error('You must be logged in to upload music');
      navigate('/sign-in');
    }
  }, [auth.currentUser, navigate]);

  const handleChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (audio files only)
      const validTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a valid audio file (MP3, WAV, OGG)');
        setMusicFile(null);
        return;
      }
      setError(null);
      setMusicFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Double-check authentication before proceeding
    if (!auth.currentUser) {
      setError('You must be logged in to upload music');
      navigate('/sign-in');
      return;
    }

    // Validate inputs
    if (!formData.title) {
      setError('Please enter a title');
      return;
    }
    if (!musicFile) {
      setError('Please upload an audio file');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // Upload file to Firebase Storage
      const storage = getStorage();
      const fileName = `${auth.currentUser.uid}-${Date.now()}-${musicFile.name}`;
      const storageRef = ref(storage, `music/${auth.currentUser.uid}/${fileName}`);
      const uploadTask = uploadBytesResumable(storageRef, musicFile);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Error uploading file:', error);
          setError('Failed to upload music file: ' + error.message);
          setUploading(false);
          setUploadProgress(0);
        },
        async () => {
          try {
            // Get the download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            // Save metadata to Firestore
            const musicData = {
              title: formData.title,
              url: downloadURL,
              userRef: auth.currentUser.uid,
              timestamp: serverTimestamp(),
            };

            await addDoc(collection(db, 'music'), musicData);
            toast.success('Music uploaded successfully!');
            setUploading(false);
            setUploadProgress(0);
            setFormData({ title: '' });
            setMusicFile(null);
            navigate('/profile');
          } catch (error) {
            console.error('Error saving music to Firestore:', error);
            setError('Failed to save music: ' + error.message);
            setUploading(false);
            setUploadProgress(0);
          }
        }
      );
    } catch (err) {
      console.error('Error during upload process:', err);
      setError('Failed to upload music: ' + err.message);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-black py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gray-900 rounded-xl shadow-md overflow-hidden">
          <div className="p-6 md:p-8">
            <h1 className="text-3xl font-bold text-white mb-8 text-center">
              Upload Music
            </h1>

            {error && (
              <div className="mb-6 text-center text-red-500">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Title Input */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-1">
                    Music Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter music title"
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                    disabled={uploading}
                  />
                </div>

                {/* File Upload */}
                <div>
                  <label htmlFor="musicFile" className="block text-sm font-medium text-gray-300 mb-1">
                    Upload Music File (MP3, WAV, OGG)
                  </label>
                  <input
                    type="file"
                    id="musicFile"
                    accept="audio/mpeg,audio/wav,audio/ogg"
                    onChange={handleFileChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-md text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-sky-400 file:text-white hover:file:bg-sky-500"
                    disabled={uploading}
                  />
                  {musicFile && (
                    <p className="mt-2 text-sm text-gray-400">
                      Selected file: {musicFile.name}
                    </p>
                  )}
                </div>

                {/* Upload Progress */}
                {uploading && (
                  <div className="w-full bg-gray-700 rounded-full h-4">
                    <div
                      className="bg-sky-400 h-4 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                    <p className="text-center text-sm text-gray-300 mt-2">
                      Uploading: {Math.round(uploadProgress)}%
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    disabled={uploading}
                    className={`w-full bg-sky-400 text-white uppercase px-4 py-3 text-sm font-medium rounded-full shadow-md transition duration-150 ease-in-out ${
                      uploading
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-sky-500 hover:shadow-lg active:bg-sky-600'
                    }`}
                  >
                    <div className="flex justify-center items-center">
                      <FcMusic className="mr-2 text-3xl bg-white rounded-full p-1 border-2" />
                      <span>{uploading ? 'Uploading...' : 'Upload Music'}</span>
                    </div>
                  </button>
                </div>

                {/* Cancel Button */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/profile')}
                    className="mt-4 text-gray-400 hover:text-gray-300 font-medium transition"
                    disabled={uploading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}